import React from 'react';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowOutputBox from './components/WorkflowOutputBox';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';

const reqSteps = [
  { label: 'Part1', title: 'RFPを作成する', path: '/devdocs/workflow/req-step1', active: true },
  { label: 'Part2', title: 'AI対話で仕様を策定する', path: '/devdocs/workflow/req-step2' },
  { label: 'Part3', title: 'proposal/businessを出力する', path: '/devdocs/workflow/req-step3' },
  { label: 'Part4', title: 'システムユースケースを出力する', path: '/devdocs/workflow/req-step4' },
];

const WorkflowReqStep1: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="要件定義編 Part1 — RFPを作成する"
      seriesName="要件定義編"
      tags={['Requirements', 'RFP']}
      seriesSteps={reqSteps}
      prevLink={{
        label: 'Kiro基本編 Part4: ワークフロー全体像',
        path: '/devdocs/workflow/kiro-basics-4',
      }}
      nextLink={{ label: 'Part2: AI対話で仕様策定', path: '/devdocs/workflow/req-step2' }}
    >
      {/* この記事で学べること */}
      <WorkflowLearningPoints
        items={[
          'RFP（要件草案）の役割と位置づけ',
          'RFPファイルの配置先と命名規則',
          'Steeringと連携するためのRFPの書き方',
          '仕様が固まっている場合・ふわっとしている場合の書き分け',
        ]}
      />

      {/* 目次 */}
      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '概要' },
          { id: 'section-2', label: '利用するファイル' },
          { id: 'section-3', label: 'RFPの書き方' },
          { id: 'section-4', label: '記載すべき内容' },
          { id: 'section-5', label: 'ベストプラクティス' },
          { id: 'section-6', label: 'アンチパターン' },
        ]}
      />

      {/* 概要 */}
      <WorkflowSection title="概要" id="section-1" number={1}>
        <WorkflowTable
          headers={['項目', '内容']}
          rows={[
            [
              '前提条件',
              'Kiroの基本操作を理解している（Kiro基本編を読み終わっている）。開発したい機能のイメージがぼんやりとでもある',
            ],
            [
              '完了条件',
              '.rfp.md ファイルが作成されている。機能の概要・対象ユーザー・主要な操作が記載されている（詳細が曖昧でもOK）',
            ],
          ]}
        />
        <WorkflowText>
          RFP（Request for Proposal）は、開発したい機能の「最初のアイデア」を文書化したものです。
          このパートでは、人間が機能のイメージを .rfp.md
          ファイルに書き出します。この時点では完璧な仕様は不要です。AIとの対話で詳細を詰めていくための「たたき台」として機能します。
        </WorkflowText>
      </WorkflowSection>

      {/* 利用するファイル */}
      <WorkflowSection title="利用するファイル" id="section-2" number={2}>
        <WorkflowTable
          headers={['項目', '内容']}
          rows={[
            ['配置先', '.kiro/requirements/{機能名}/'],
            ['ファイル名', '{機能名}.rfp.md'],
            ['例', '.kiro/requirements/member_management/partner.rfp.md'],
          ]}
        />
      </WorkflowSection>

      {/* RFPの書き方 */}
      <WorkflowSection title="RFPの書き方" id="section-3" number={3}>
        <WorkflowText>
          RFPに決まったテンプレートはありません。自由形式で記載できますが、以下の情報を含めるとAIとの対話がスムーズに進みます。
        </WorkflowText>
        <WorkflowCodeBlock
          title="RFP テンプレート"
          language="markdown"
          collapsible
          defaultCollapsed={false}
          code={`# {機能名} の要件草案

## ゴール

- 対話を行いながら {機能名} の草案をまとめる。
- 決定した内容をもとに \`{機能名}.proposal.md\` を作成する。
- 決定した内容をもとに \`{機能名}.business.md\` を作成する。

## 進め方

- あなた が 私 に質問をして、それに回答する形でゴールを目指す
- 私 の回答に矛盾や設計上の問題がある場合は、遠慮せず指摘すること。Yesマンにはならない。
- 代替案がある場合はメリット・デメリットと、あなたはどちらが良いと思うかも添えて提示すること。
- わからないことは推測せずに聞くこと。
- 決定が曖昧なまま先に進まないこと。`}
        />

        {/* 必ず書くべき項目 */}
        <WorkflowSection title="必ず書くべき項目" subNumber="3.1">
          <WorkflowText>
            RFPは自由形式ですが、以下の項目は必ず含めてください。これらが欠けるとAIが正しく動作しません。
          </WorkflowText>
          <WorkflowTable
            headers={['項目', '記載例', '書かないとどうなるか']}
            rows={[
              [
                '{機能名}.proposal.md を作成する（ゴール）',
                '決定した内容をもとに partner.proposal.md を作成する。',
                'proposal生成ルール（Steering）が参照されず、何を決めるべきか（テーブル設計・API設計等）をAIが把握できない',
              ],
              [
                '{機能名}.business.md を作成する（ゴール）',
                '決定した内容をもとに partner.business.md を作成する。',
                'business生成ルール（Steering）が参照されず、ユーザー操作手順や入力項目の確認が行われない',
              ],
              [
                '対話形式の指定（進め方）',
                'あなた が 私 に質問をして、それに回答する形でゴールを目指す',
                'AIが受動的になり、人間が全て書き出さないと対話が始まらない',
              ],
              [
                '対話姿勢のルール（進め方）',
                '矛盾や問題がある場合は指摘すること',
                'AIがYesマンになり、設計上の問題を見逃す',
              ],
            ]}
          />
          <WorkflowInfoBox>
            RFPの「進め方」に書くのは「対話の進め方のルール」（AIにどう振る舞ってほしいか）だけです。「具体的に何を決めるか」（テーブル設計の項目、API仕様の確認事項等）は、作成対象のファイル（proposal.md
            /
            business.md）に対応するSteeringルールが自動で制御します。人間はRFPで対話の姿勢だけ指示すれば、あとはSteeringが補完してくれます。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 記載すべき内容 */}
      <WorkflowSection title="記載すべき内容" id="section-4" number={4}>
        <WorkflowSection title="仕様がある程度固まっている場合" subNumber="4.1">
          <WorkflowList
            items={[
              '機能の目的・背景',
              '必要なAPI一覧（エンドポイント、メソッド）',
              'テーブル設計の候補',
              'ビジネスルール',
              '認証・認可の要件',
              '関連する既存機能との関係',
            ]}
          />
          <WorkflowCodeBlock
            title="記載例: client.rfp.md（仕様がある程度固まっている場合）"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# client 機能の要件草案

## ゴール

{テンプレートの通り}

## 進め方

{テンプレートの通り}

## 要員管理サブシステムイメージ

- client は 要員管理サブシステム (member_management) の1機能
- 要員管理サブシステムは下記の機能を想定している。
    - partner(パートナー)
    - member(要員)
    - client (取引先)
    - project (プロジェクト)
    - contract (契約)
    - contract-request (契約申請)
- partner は パートナー企業 の管理機能
- member は 要員 の管理機能
- client は 取引先企業 の管理機能
- project は プロジェクト の管理機能
- 取引先企業 は 複数の プロジェクト を持つ
- プロジェクト に 要員 を 参画/脱退 させるための機能が 契約申請 となる。
- 契約申請 が承認後に 契約 となる。
- 契約 は プロジェクト と メンバー 間で締結されるもので1契約ごとに 原価 と 売上 がある。
- 契約 を集計することで会社全体のコスト・売上・利益の内訳を集計する。

## client イメージ

### 基本情報

- id - PK:uuid(自動採番)
- 名前、カナ - 必須

### 法人情報

- 法人番号 - 任意
- 登録番号 - 任意:Tから始まるインボイスの番号
- 住所 - 任意
- 企業ホームページ - 任意

### 連絡先

- 担当者名 - 任意
- 電話番号 - 任意
- メールアドレス - 任意

### 取引期間

- 取引開始日 - 必須
- 取引終了日 - 任意:NULLの場合は契約中の扱い

### 備考

- 備考 - 任意

### メタ情報

- 登録日
- 登録者

他に必要そうな項目や、あった方が良い項目があれば提案して`}
          />
          <WorkflowInfoBox>
            仕様が固まっている場合のポイント:
            関連機能との関係図を書く（AIがエンティティ間のリレーションを理解できる）/
            データ項目は必須/任意を明記する（確認の往復が減る）/
            「提案して」と明記する（AIが受動的にならず足りない項目を提案してくれる）
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="ふわっとしている場合" subNumber="4.2">
          <WorkflowList
            items={[
              '何を実現したいか（1〜3文）',
              '誰が使うか',
              'どんなデータを扱うか',
              '既存機能との関連（あれば）',
            ]}
          />
          <WorkflowCodeBlock
            title="記載例: 通知機能のRFP（ふわっとしている場合）"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# 通知機能 の要件草案

## ゴール

{テンプレートの通り}

## 進め方

{テンプレートの通り}

## やりたいこと

- ユーザーへの通知を管理者が登録・配信できるようにしたい。
- ユーザーは自分宛ての通知を確認して、対応済みにできるようにしたい。

## 誰が使うか

- 管理者（通知の登録・削除）
- 全ユーザー（通知の確認・対応）

## 扱うデータ

- 通知（タイトル、本文、種別、期限、宛先）

## 既存機能との関連

- ユーザー管理機能（通知の宛先としてユーザーを指定する）`}
          />
          <WorkflowInfoBox>
            ふわっとしている場合のポイント:
            「誰が」「何を」だけは書く（これが無いとAIが最初の質問すら組み立てられない）/
            完璧を目指さない（3行でもOK。対話で膨らませるのがSteeringの役割）/
            既存機能との関連を一言添える（AIが既存設計書を参照する起点になる）
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* ベストプラクティス */}
      <WorkflowSection title="ベストプラクティス" id="section-5" number={5}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: 'RFPには「対話の進め方」だけを書き、成果物の構造やルールはSteeringに任せる',
              effect:
                '責務が分離され、RFPがシンプルになる。Steeringのルール更新がRFP修正なしで全機能に反映される',
            },
            {
              practice: '関連するサブシステム全体の構造を冒頭に書く',
              effect: 'AIが個別機能を全体の中で位置づけて設計できる',
            },
            {
              practice: 'データ項目に必須/任意・型のヒントを付ける',
              effect: '確認の往復が減り、1回の対話で深い議論に入れる',
            },
            {
              practice: '「提案して」「他にあれば教えて」と明記する',
              effect: 'AIが受動的にならず、経験に基づいた提案をしてくれる',
            },
            {
              practice: '既存の類似機能のRFPやproposalのパスを示す',
              effect: 'AIが既存設計を参照して一貫性のある提案ができる',
            },
          ]}
        />
      </WorkflowSection>

      {/* アンチパターン */}
      <WorkflowSection title="アンチパターン" id="section-6" number={6}>
        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice:
                'proposal.mdに書くべき内容（テーブル定義のテンプレート、API仕様の書き方等）をRFPに書く',
              effect:
                'Steeringのルールと二重管理になり、矛盾が発生する。RFPが肥大化して対話の起点としての役割がぼやける',
            },
            {
              practice: 'ゴールや進め方を書かずに機能情報だけ書く',
              effect:
                'Steeringが発動しても対話の方向性が定まらず、AIが何から聞けば良いか分からない',
            },
            {
              practice: '1つのRFPに複数機能を詰め込む',
              effect:
                '対話が肥大化しコンテキスト枠を超える。proposal/businessも1機能1ファイルなので分ける',
            },
            {
              practice: 'AIの回答をそのまま全て承認する',
              effect: '設計の矛盾に気づかないまま進み、後工程で手戻りが発生する',
            },
            {
              practice: '既存設計書との関連を一切書かない',
              effect: 'AIが孤立した設計をしてしまい、テーブル名やAPI IDが既存と衝突する',
            },
          ]}
        />
      </WorkflowSection>

      {/* 成果物 */}
      <WorkflowOutputBox>
        <WorkflowTable
          headers={['成果物', '説明', '利用先']}
          rows={[['*.rfp.md', '機能の要件草案', 'Part2（AI対話で仕様策定）の入力']]}
        />
      </WorkflowOutputBox>
    </WorkflowArticleLayout>
  );
};

export default WorkflowReqStep1;
